<!-- eslint-disable max-len -->
<template>
  <div :style="styles.player">
    <template v-if="isValid">
      <iframe
        id="codec"
        ref="embedded-video-player"
        class="embeddedVideo"
        loading="lazy"
        sandbox="allow-forms allow-scripts allow-pointer-lock allow-same-origin allow-top-navigation allow-presentation"
        allowfullscreen
        frameBorder="0"
        :src="videoSrc"></iframe>
    </template>
    <template v-else>
      <p>Error loading video.</p>
    </template>
  </div>
</template>
<script>
// eslint-disable no-useless-escape
import postscribe from "postscribe";
import Vimeo from "@vimeo/player";
import { utils } from "~/Scripts/vue/mixins/common";

const VIDEO_HOST = {
  VIMEO: "player::vimeo",
  DAILY_MOTION: "player::dailymotion",
  YOU_TUBE: "player::youtube",
};

export default {
  name: "EmbeddedVideo",
  mixins: [utils],
  props: {
    css: {
      type: String,
      default: "embed-responsive-16by9",
    },
    src: { type: String, required: true },
    params: {
      type: Object,
      default: () => ( {} ),
    },
  },
  data: () => ( {
    iFrame: undefined,
    VIDEO_HOST,
    url: "",
    styles: {
      player: {
        position: "relative",
        overflow: "hidden",
      },
    },
    hosts: {
      [VIDEO_HOST.YOU_TUBE]: {
        // reg: /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|\?v=)([^#\&\?]*).*/i,
        // reg: /^.*(?:(?:v|vi|be|videos|embed)\/(?!videoseries)|(?:v|ci)=)([\w-]{11})/i,
        patterns: [
          // eslint-disable-next-line no-useless-escape
          /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube\.com|youtu.be))(\/(?:[\w\-]+\?v=|embed\/|v\/)?)([\w\-]+)(\S+)?$/i,
        ],
        url: "https://www.youtube.com/embed/$5",
        params: {
          "picture-in-picture": 1,
          accelerometer: 1,
          gyroscope: 1,
        },
        name: VIDEO_HOST.YOU_TUBE,
      },
      [VIDEO_HOST.VIMEO]: {
        patterns: [/^.*vimeo.com\/(\d+)($|\/|\b)/i, /^.*player.vimeo.com\/video\/(\d+)($|\/|\b)/i],
        url: "https://player.vimeo.com/video/$1",
        params: {
          title: 0,
          byline: 0,
          portrait: 0,
        },
        name: VIDEO_HOST.VIMEO,
      },
      [VIDEO_HOST.DAILY_MOTION]: {
        // eslint-disable-next-line no-useless-escape
        patterns: [/^.*(?:\/video|dai.ly)\/([A-Za-z0-9]+)([^#\&\?]*).*/i],
        url: "https://www.dailymotion.com/embed/video/$1",
        params: {
          autoplay: 0,
        },
        name: VIDEO_HOST.DAILY_MOTION,
      },
    },
    vimeoPlayer: null,
    scripts: {
      // eslint-disable-next-line no-useless-escape
      vimeo: "<script src='https://player.vimeo.com/api/player.js'><\/script>",
    },
    callbacks: ["play", "pause", "error", "seeked", "ended", "loaded"],
  } ),
  computed: {
    isVimeo () {
      return this.host && this.host.name === VIDEO_HOST.VIMEO;
    },
    host () {
      // eslint-disable-next-line
      const hostKey = Object.keys(this.hosts).find(key => this.hosts[key].patterns.find( regex => regex.test( this.src ) ),
      );
      return this.hosts[hostKey];
    },
    videoSrc () {
      let url = null;

      if ( this.host ) {
        const regex = this.host.patterns.find( r => r.exec( this.src ) );

        if ( regex ) {
          const params = { ...this.host.params, ...this.params };
          const query = Object.keys( params )
            .map( key => `${key}=${params[key]}` )
            .join( "&" );
          const and = this.host.url.indexOf( "?" ) >= 0 ? "&" : "?";
          url = this.src.replace( regex, this.host.url ) + and + query;
        }
      }

      // if a host cannot be found assume link is other form of content
      return url || this.src;
    },
    isValid () {
      return this.videoSrc != null;
    },
  },
  mounted () {
    this.iFrame = this.$refs["embedded-video-player"];

    // evaluate any overidden styles. Default ratio 16:9
    const classes = this.$el.getAttribute( "class" );
    if ( this.isNullOrEmpty( classes ) ) {
      this.$el.setAttribute( "class", "embeddedVideo__16x9" );
    }

    // store and create vimeo callbacks
    if ( this.isVimeo ) {
      this.setVimeoPlayer( this.iFrame )
        .then( player => {
          this.callbacks.forEach( event => player.on( event, async data => {
            let videoTitle;
            if ( event === "loaded" ) {
              videoTitle = await player.getVideoTitle();
            }
            this.$emit( event, { callback: event, ...data, videoTitle } );
          } ),
          );
        } )
        .catch( err => {
          console.error( `Failed to create Vimeo callbacks ${err}` );
        } );
    }
  },
  methods: {
    async setVimeoPlayer ( iFrame ) {
      return new Promise( ( resolve, reject ) => {
        try {
          postscribe( "#codec", this.scripts.vimeo );
          this.vimeoPlayer = new Vimeo( iFrame );
          resolve( this.vimeoPlayer );
        } catch ( err ) {
          reject( err );
        }
      } );
    },
  },
};
</script>
<style lang="less" scoped>
.embeddedVideo {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  &__16x9 {
    width: 100%;
    padding-top: 56.25%; /* 16:9 Aspect Ratio (divide 9 by 16 = 0.5625) */
  }
}
</style>
